import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Menu, Moon, Sun, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/lib/theme";
import { Wordmark } from "@/components/wordmark";

const nav = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "Founder" },
  { to: "/faq", label: "FAQ" },
  { to: "/demo", label: "Demo" },
] as const;

function ThemeToggle() {
  const { resolved, setTheme } = useTheme();
  return (
    <button
      type="button"
      onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
      aria-label="Toggle theme"
      className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      {resolved === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function MarketingShell({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header
        className={cn(
          "sticky top-0 z-40 transition-all duration-300",
          scrolled
            ? "border-b border-border/60 bg-background/70 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent",
        )}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <Link to="/" className="flex items-center" aria-label="Super Affiliate OS, home">
            <Wordmark />
          </Link>
          <nav className="hidden items-center gap-7 md:flex">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className={cn(
                  "text-sm text-muted-foreground/80 transition-opacity hover:opacity-100",
                  path === n.to ? "text-foreground opacity-100" : "opacity-80",
                )}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            <ThemeToggle />
            <Link
              to="/login"
              className="px-3 text-sm text-muted-foreground hover:text-foreground"
            >
              Sign in
            </Link>
            <Button asChild size="sm">
              <Link to="/signup">Start free trial</Link>
            </Button>
          </div>
          <button
            type="button"
            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-xl md:hidden">
          <div className="flex h-16 items-center justify-between px-6">
            <Wordmark />
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-1 flex-col gap-6 px-6 py-10">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                className="text-2xl font-medium tracking-tightest"
              >
                {n.label}
              </Link>
            ))}
            <div className="mt-auto flex flex-col gap-3">
              <ThemeToggle />
              <Button asChild variant="outline">
                <Link to="/login" onClick={() => setMobileOpen(false)}>Sign in</Link>
              </Button>
              <Button asChild>
                <Link to="/signup" onClick={() => setMobileOpen(false)}>Start free trial</Link>
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border/60 py-16">
        <div className="mx-auto flex max-w-7xl flex-col gap-12 px-6">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Wordmark />
              <p className="mt-4 max-w-xs text-sm text-muted-foreground">
                The operating system for serious affiliate marketers.
              </p>
            </div>
            <FooterCol
              className="lg:col-span-2"
              heading="Product"
              links={[
                { to: "/features", label: "Features" },
                { to: "/pricing", label: "Pricing" },
                { to: "/faq", label: "FAQ" },
                { to: "/features", label: "Roadmap" },
                { to: "/features", label: "Changelog" },
              ]}
            />
            <FooterCol
              className="lg:col-span-2"
              heading="Company"
              links={[
                { to: "/about", label: "Founder's Note" },
                { to: "/about", label: "Affiliate Program" },
                { to: "/contact", label: "Contact" },
              ]}
            />
            <FooterCol
              className="lg:col-span-2"
              heading="Legal"
              links={[
                { to: "/privacy", label: "Privacy" },
                { to: "/terms", label: "Terms" },
                { to: "/cookies", label: "Acceptable Use" },
                { to: "/cookies", label: "Refund Policy" },
              ]}
            />
            <div className="lg:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Subscribe
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Affiliate intelligence, weekly. Substance over noise.
              </p>
              <form
                className="mt-3 flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  (e.currentTarget as HTMLFormElement).reset();
                }}
              >
                <input
                  type="email"
                  required
                  placeholder="you@domain.com"
                  className="flex-1 min-w-0 rounded-md border border-border bg-card px-3 py-2 text-xs outline-none focus:border-primary"
                />
                <button
                  type="submit"
                  className="rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:opacity-90"
                >
                  Join
                </button>
              </form>
              <p className="mt-2 text-[10px] text-muted-foreground">Unsubscribe anytime.</p>
            </div>
          </div>
          <div className="border-t border-border/60 pt-6 text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} Super Affiliate OS · Built by a super affiliate, for super affiliates.
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({
  heading,
  links,
  className,
}: {
  heading: string;
  links: readonly { to: string; label: string }[];
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-xs font-semibold uppercase tracking-wider text-foreground">{heading}</div>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
        {links.map((l, i) => (
          <li key={`${l.to}-${i}`}>
            <Link to={l.to} className="hover:text-foreground">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
